import { FC, useEffect, useState } from 'react';
import { Alert, Badge, Card, Col, Row, Spinner, Table } from 'react-bootstrap';

type Provenance = {
  filename?: string | null;
  version?: string | null;
  checksum?: string | null;
  type?: string | null;
  kind?: string | null;
  artifact_key?: string | null;
  confidence?: string | null;
};

type ImportRecord = {
  id: number;
  name?: string;
  kind?: string;
  status?: string;
  state?: string;
  rows?: number;
  source: Provenance;
  readiness: { ready: boolean; label?: string; errors?: number; warnings?: number; message?: string | null };
};

type ImportLane = {
  read_only: boolean;
  batch_count: number;
  candidate_count: number;
  batches: ImportRecord[];
  candidates: ImportRecord[];
  visible?: boolean;
  inactive_until_routed?: boolean;
};

type ImportContract = {
  scope: { company: { name: string }; group: { name: string } };
  assigned: ImportLane;
  unassigned: ImportLane;
};

const sourceLabel = (record: ImportRecord) =>
  record.source.filename ?? record.source.artifact_key ?? record.source.kind ?? record.source.type ?? 'Recorded source';

const laneRows = (lane: ImportLane) => [...lane.batches, ...lane.candidates].slice(0, 8);

const ImportLaneCard: FC<{ title: string; lane: ImportLane; unassigned?: boolean }> = ({ title, lane, unassigned = false }) => (
  <Card className="custom-card h-100">
    <Card.Header className="d-flex align-items-center justify-content-between gap-2">
      <div className="card-title mb-0">{title}</div>
      <Badge bg={unassigned ? 'warning' : 'primary'}>{lane.batch_count} packages · {lane.candidate_count} candidates</Badge>
    </Card.Header>
    <Card.Body>
      {unassigned && (
        <Alert variant="warning" className="py-2">
          Company Admin only. These records are read-only and inactive until they are explicitly routed to a Schedule Group.
        </Alert>
      )}
      {lane.batch_count + lane.candidate_count === 0 ? (
        <p className="text-muted mb-0">No {unassigned ? 'unassigned' : 'group-assigned'} import records are available.</p>
      ) : (
        <div className="table-responsive">
          <Table className="table text-nowrap mb-0" size="sm">
            <thead><tr><th>Record</th><th>Provenance</th><th>Readiness</th></tr></thead>
            <tbody>
              {laneRows(lane).map(record => (
                <tr key={`${record.name ? 'batch' : 'candidate'}-${record.id}`}>
                  <td><strong>{record.name ?? record.kind ?? `Candidate ${record.id}`}</strong><div className="text-muted fs-11">{record.status ?? record.state}</div></td>
                  <td>{sourceLabel(record)}<div className="text-muted fs-11">{record.source.version ?? record.source.checksum?.slice(0, 12) ?? 'Version not supplied'}</div></td>
                  <td><Badge bg={record.readiness.ready && !unassigned ? 'success' : 'warning'}>{unassigned ? 'Routing required' : (record.readiness.label ?? (record.readiness.ready ? 'Ready' : 'Review required'))}</Badge></td>
                </tr>
              ))}
            </tbody>
          </Table>
          {lane.batch_count + lane.candidate_count > 8 && <p className="text-muted fs-11 mt-2 mb-0">Showing 8 of {lane.batch_count + lane.candidate_count} records.</p>}
        </div>
      )}
    </Card.Body>
  </Card>
);

const ImportContractPanel: FC<{ tenantQuery: string }> = ({ tenantQuery }) => {
  const [data, setData] = useState<ImportContract | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    setData(null);
    setError(null);
    setLoading(true);

    fetch(`/api/v1/secure/schedule-settings/imports?${tenantQuery}`, { credentials: 'same-origin', headers: { Accept: 'application/json' }, signal: controller.signal })
      .then(async response => {
        if (response.status === 403) throw new Error('You are not authorized to view imports for this company and department.');
        if (!response.ok) throw new Error(`Imports could not be loaded (${response.status}).`);
        return response.json() as Promise<ImportContract>;
      })
      .then(setData)
      .catch(requestError => {
        if (requestError instanceof DOMException && requestError.name === 'AbortError') return;
        setError(requestError instanceof Error ? requestError.message : 'Imports could not be loaded.');
      })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });

    return () => controller.abort();
  }, [tenantQuery]);

  if (loading) return <Alert variant="light"><Spinner animation="border" size="sm" className="me-2" />Loading assigned and unassigned import contracts.</Alert>;
  if (error) return <Alert variant="danger">{error}</Alert>;
  if (!data) return <Alert variant="secondary">No import contract is available.</Alert>;

  return (
    <Row className="g-3 mb-3">
      <Col xl={data.unassigned.visible ? 7 : 12}><ImportLaneCard title={`Assigned to ${data.scope.group.name}`} lane={data.assigned} /></Col>
      {data.unassigned.visible && <Col xl={5}><ImportLaneCard title="Unassigned onboarding" lane={data.unassigned} unassigned /></Col>}
    </Row>
  );
};

export default ImportContractPanel;
